import { axiosConfig } from "@/services/axiosConfig";
import type { Teacher } from "@/projects/lib/store";
import {
  buildPageParams,
  extractList,
  extractPaginationMeta,
  type PaginatedResult,
} from "@/projects/lib/pagination";

const TEACHERS_PATH = "/rajnagar_gyanmahostav/teachers/";

export type ApiTeacher = {
  id?: string | number;
  pk?: string | number;
  teacher_id?: string | number;
  teacher_name?: string;
  user_id?: string | number;
  name?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile?: string;
  phone?: string;
  contact_number?: string;
  subject?: string;
  subject_department?: string;
  username?: string;
  active?: boolean;
  is_active?: boolean;
  status?: string;
  role?: string;
  /** Stats from list API */
  assigned?: number;
  pending?: number;
  reward?: number;
  not_proper?: number;
  /** Legacy / alternate stat field names */
  assigned_count?: number;
  pending_count?: number;
  reward_count?: number;
  not_proper_count?: number;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export type TeacherWritePayload = {
  teacher_name: string;
  teacher_id: string;
  email: string;
  mobile?: string;
  subject_department: string;
  username: string;
  password?: string;
  confirm_password?: string;
  is_active?: boolean;
  active?: boolean;
};

export type TeacherWithStats = Teacher & {
  /** API `assigned` count */
  assigned: number;
  /** @deprecated use `assigned` — kept for older UI refs */
  total: number;
  pending: number;
  reward: number;
  notProper: number;
};

function unwrapOne(payload: ApiTeacher | { data?: ApiTeacher }): ApiTeacher {
  if (payload && typeof payload === "object" && "data" in payload && payload.data) {
    return payload.data;
  }
  return payload as ApiTeacher;
}

function teacherName(row: ApiTeacher): string {
  const full =
    row.teacher_name?.trim() || row.full_name?.trim() || row.name?.trim();
  if (full) {
    return full;
  }
  const parts = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return parts || row.email || row.username || "Teacher";
}

function isActive(row: ApiTeacher): boolean {
  if (typeof row.active === "boolean") {
    return row.active;
  }
  if (typeof row.is_active === "boolean") {
    return row.is_active;
  }
  if (typeof row.status === "string") {
    const s = row.status.toLowerCase();
    if (["active", "enabled"].includes(s)) {
      return true;
    }
    if (["inactive", "disabled", "deactivated"].includes(s)) {
      return false;
    }
  }
  return true;
}

function pickStat(row: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (!(key in row)) {
      continue;
    }
    const v = row[key];
    if (v === null || v === undefined || v === "") {
      continue;
    }
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isNaN(n)) {
      return n;
    }
  }
  return 0;
}

export function mapApiTeacherToTeacher(row: ApiTeacher): TeacherWithStats {
  const raw = row as Record<string, unknown>;
  const teacherCode =
    row.teacher_id !== null && row.teacher_id !== undefined ? String(row.teacher_id) : undefined;
  const userId = row.user_id !== null && row.user_id !== undefined ? String(row.user_id) : undefined;
  const id = String(
    row.id ?? row.pk ?? userId ?? teacherCode ?? row.email ?? teacherName(row)
  );
  const assigned = pickStat(raw, "assigned", "assigned_count");
  const pending = pickStat(raw, "pending", "pending_count");
  const reward = pickStat(raw, "reward", "reward_count");
  const notProper = pickStat(raw, "not_proper", "not_proper_count");

  return {
    id,
    userId,
    teacherId: teacherCode,
    name: teacherName(row),
    email: row.email ?? "",
    mobile: row.mobile ?? row.phone ?? row.contact_number ?? "",
    subject: row.subject_department ?? row.subject ?? "",
    username: row.username ?? row.email ?? id,
    active: isActive(row),
    assigned,
    total: assigned,
    pending,
    reward,
    notProper,
  };
}

function teacherDetailPath(id: string) {
  return `${TEACHERS_PATH}${encodeURIComponent(id)}/`;
}

function mapApiTeacherRow(item: unknown): TeacherWithStats {
  {
    const row = (item ?? {}) as Record<string, unknown>;
    const teacherNameValue =
      (typeof row.teacher_name === "string" && row.teacher_name) ||
      (typeof row.name === "string" && row.name) ||
      "Teacher";
    const teacherCode =
      row.teacher_id !== null && row.teacher_id !== undefined ? String(row.teacher_id) : undefined;
    const userId = row.user_id !== null && row.user_id !== undefined ? String(row.user_id) : undefined;
    const id = String(
      row.id ?? row.pk ?? userId ?? teacherCode ?? row.email ?? teacherNameValue
    );

    const assigned = Number(row.assigned ?? row.assigned_count ?? 0);
    const pending = Number(row.pending ?? row.pending_count ?? 0);
    const reward = Number(row.reward ?? row.reward_count ?? 0);
    const notProper = Number(row.not_proper ?? row.not_proper_count ?? 0);

    const status =
      typeof row.status === "string" ? row.status.toLowerCase() : "";
    const active =
      typeof row.active === "boolean"
        ? row.active
        : typeof row.is_active === "boolean"
          ? row.is_active
          : !["inactive", "disabled", "deactivated"].includes(status);

    return {
      id,
      userId,
      teacherId: teacherCode,
      name: teacherNameValue,
      email: typeof row.email === "string" ? row.email : "",
      mobile:
        typeof row.mobile === "string"
          ? row.mobile
          : typeof row.phone === "string"
            ? row.phone
            : "",
      subject:
        typeof row.subject_department === "string"
          ? row.subject_department
          : typeof row.subject === "string"
            ? row.subject
            : "",
      username:
        typeof row.username === "string"
          ? row.username
          : typeof row.email === "string"
            ? row.email
            : id,
      active,
      assigned: Number.isFinite(assigned) ? assigned : 0,
      total: Number.isFinite(assigned) ? assigned : 0,
      pending: Number.isFinite(pending) ? pending : 0,
      reward: Number.isFinite(reward) ? reward : 0,
      notProper: Number.isFinite(notProper) ? notProper : 0,
    };
  }
}

export async function fetchTeachersPage(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<PaginatedResult<TeacherWithStats>> {
  const query: Record<string, string> = {
    ...buildPageParams(params?.page, params?.pageSize),
  };
  if (params?.search) {
    query.search = params.search;
  }
  const { data } = await axiosConfig.get(TEACHERS_PATH, {
    params: query,
    headers: { Accept: "application/json" },
  });
  const items = extractList<unknown>(data).map(mapApiTeacherRow);
  return {
    items,
    pagination: extractPaginationMeta(data, items.length, params?.page ?? 1),
  };
}

export async function fetchTeachers(): Promise<TeacherWithStats[]> {
  const { items } = await fetchTeachersPage({ pageSize: 1000 });
  return items;
}

export async function createTeacher(
  payload: TeacherWritePayload
): Promise<TeacherWithStats> {
  const { data } = await axiosConfig.post<ApiTeacher | { data: ApiTeacher }>(
    TEACHERS_PATH,
    payload,
    { headers: { "Content-Type": "application/json", Accept: "application/json" } }
  );
  return mapApiTeacherToTeacher(unwrapOne(data));
}

export async function updateTeacher(
  id: string,
  payload: Partial<TeacherWritePayload>
): Promise<TeacherWithStats> {
  const { data } = await axiosConfig.patch<ApiTeacher | { data: ApiTeacher }>(
    teacherDetailPath(id),
    payload,
    { headers: { "Content-Type": "application/json", Accept: "application/json" } }
  );
  return mapApiTeacherToTeacher(unwrapOne(data));
}

export async function setTeacherActive(
  id: string,
  active: boolean
): Promise<TeacherWithStats> {
  return updateTeacher(id, { is_active: active, active });
}

export async function deleteTeacher(id: string): Promise<void> {
  await axiosConfig.delete(teacherDetailPath(id), {
    headers: { Accept: "application/json" },
  });
}

export function getApiErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error", "non_field_errors"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
    if (Array.isArray(v) && typeof v[0] === "string") {
      return v[0];
    }
  }
  // Field-level errors: { email: ["..."] }
  for (const [k, v] of Object.entries(data)) {
    if (Array.isArray(v) && typeof v[0] === "string") {
      return `${k}: ${v[0]}`;
    }
    if (typeof v === "string" && v.trim() && k !== "status" && k !== "success") {
      return v;
    }
  }
  return fallback;
}
